import { LatLng, NavEdge, ZoneType } from '../types/map';
import { Signal, SignalSource, MovementBuff } from '../types/game';
import { tuning } from '../data/tuning';

// --- Signal strength → radius mapping ---
// Higher signal strength = smaller (more precise) radius
// Uses a smooth curve controlled by tuning params instead of hard brackets.
// Label thresholds kept for UI display.

/** Get a human-readable label for a given signal strength */
export function getSignalLabel(strength: number): string {
  if (strength >= 0.8) return 'hot';
  if (strength >= 0.6) return 'strong';
  if (strength >= 0.4) return 'moderate';
  if (strength >= 0.2) return 'weak';
  return 'faint';
}

// Below this threshold, no signal is generated (read from tuning at call time)

// Max age before a signal is removed (3 turns visible, then gone)
export const SIGNAL_MAX_AGE = 3;

let signalCounter = 0;

/**
 * Calculate how much exposure is gained from traversing a set of edges.
 *
 * Two sources of exposure per edge:
 * 1. Zone exposure: 1 point per meter walked in commercial, transit, mixed-use,
 *    or civic zones — multiplied by that zone's multiplier.
 * 2. Road classification bonus (per meter):
 *      major road:       0.20  (2 per 10m)
 *      minor road:       0.10  (1 per 10m)
 *      residential road: 0.05  (0.5 per 10m)
 *      footpath:         0     (free)
 *
 * Residential streets still build a trickle of exposure so long-distance
 * pure-residential routes eventually lift the player out of the blind
 * tier — otherwise a residential-only run stays blind forever and the
 * agent awareness system has nothing to react to.
 *
 * Total exposure is capped at exposureMax (1000).
 * Returns the exposure delta (always >= 0).
 */
export function calculateExposureFromEdges(edges: NavEdge[]): number {
  let exposure = 0;

  for (const edge of edges) {
    const zoneMultiplier = tuning.zoneMultipliers[edge.zone] ?? 0;

    // Zone exposure: 2 points per meter × zone multiplier (only for qualifying zones)
    if (zoneMultiplier >= tuning.exposureZoneThreshold) {
      exposure += edge.length * zoneMultiplier * 2;
    }

    // Road classification bonus
    if (edge.classification === 'major') {
      exposure += edge.length * 0.2;  // 2 per 10m
    } else if (edge.classification === 'minor') {
      exposure += edge.length * 0.1;  // 1 per 10m
    } else if (edge.classification === 'residential') {
      exposure += edge.length * 0.15; // 0.15 per m — sustained residential
                                      // walks cost something but are noticeably
                                      // quieter than major/minor roads once
                                      // you factor in crossing bonuses. Tuned
                                      // down from 0.5/m which felt punishing.
    }
  }

  return exposure;
}

/**
 * Compute the total exposure gain from a movement, applying every modifier
 * the commit-time reducers apply, in the same order. This is the single
 * source of truth for "how much exposure would this move cost" — callers
 * (hover preview banner, alt-route previews, commit-time reducer if it
 * ever gets refactored) should all route through here so the hover number
 * always matches what actually gets committed.
 *
 * Modifier stack:
 *   1. rawZoneExposure = calculateExposureFromEdges(edges) — zone + road class
 *   2. ×2 if isInVehicle AND path contains any major-road length
 *   3. ×tuning.disguiseExposureMultiplier if disguiseActive
 *   4. ×0 if lowProfileActive (zero everything)
 *   5. applyExposureDiminishing(currentExposure, …)
 *   6. + road-crossing bonus (35/major, 25/minor) with the same disguise/
 *      low-profile multipliers applied
 */
export function computeMovementExposureGain(params: {
  edges: NavEdge[];
  currentExposure: number;
  isInVehicle: boolean;
  disguiseActive: boolean;
  lowProfileActive: boolean;
}): number {
  const { edges, currentExposure, isInVehicle, disguiseActive, lowProfileActive } = params;

  if (lowProfileActive) return 0;

  let rawExpGain = calculateExposureFromEdges(edges);

  if (isInVehicle) {
    const majorLength = edges
      .filter(e => e.classification === 'major')
      .reduce((sum, e) => sum + e.length, 0);
    if (majorLength > 0) rawExpGain *= 2;
  }

  if (disguiseActive) rawExpGain *= tuning.disguiseExposureMultiplier;

  const expGain = applyExposureDiminishing(currentExposure, rawExpGain);

  // Road-crossing bonus — uses the same disguise-aware multiplier. Low
  // Profile would have already short-circuited above, so we only need to
  // factor in Disguise here.
  const crossingMult = disguiseActive ? tuning.disguiseExposureMultiplier : 1;
  let roadCrossingExp = 0;
  for (const edge of edges) {
    if (edge.classification === 'major') roadCrossingExp += 35 * crossingMult;
    else if (edge.classification === 'minor') roadCrossingExp += 25 * crossingMult;
  }

  return expGain + roadCrossingExp;
}

/**
 * Convert current exposure level to a signal multiplier.
 * Exposure is on a 0–exposureMax (1000) scale.
 * 0% = 1.0x (no effect)
 * 100% = exposureMaxEffect (e.g. 3.0x)
 * Uses linear curve — exposure scales evenly so low exposure doesn't spike signals.
 */
function exposureToMultiplier(exposure: number): number {
  if (exposure <= 0) return 1;
  const pct = Math.min(1, exposure / tuning.exposureMax);
  const maxBonus = tuning.exposureMaxEffect - 1;
  return 1 + maxBonus * pct;
}

/**
 * Apply diminishing returns to exposure gain.
 * The more exposure you already have, the less you gain from the same activity.
 * At 0% exposure: gain 100% of raw exposure.
 * At 50% exposure: gain ~70% of raw exposure.
 * At 80% exposure: gain ~45% of raw exposure.
 * Formula: effectiveGain = rawGain * (1 - currentPct^1.5)
 */
export function applyExposureDiminishing(currentExposure: number, rawGain: number): number {
  const currentPct = Math.min(1, currentExposure / tuning.exposureMax);
  const dampening = 1 - Math.pow(currentPct, 1.5);
  return rawGain * Math.max(0.1, dampening); // always gain at least 10% of raw
}

/**
 * Calculate signal strength from traversing a path of edges.
 * Returns 0-1 normalized strength. The dominant factor is road classification
 * (signalWeight on each edge), modulated by zone and card modifier.
 */
function calculatePathSignalStrength(
  edges: NavEdge[],
  cardSignalModifier: number,
): number {
  if (edges.length === 0) return 0;
  if (cardSignalModifier === 0) return 0;

  let totalWeightedSignal = 0;
  let totalLength = 0;

  for (const edge of edges) {
    const zoneMultiplier = tuning.zoneMultipliers[edge.zone] ?? 0.6;
    // Each edge contributes: its length * road signal weight * zone multiplier
    const edgeSignal = edge.length * edge.signalWeight * zoneMultiplier;
    totalWeightedSignal += edgeSignal;
    totalLength += edge.length;
  }

  if (totalLength === 0) return 0;

  // Area factor: the road type and zone you're in — this is the dominant signal driver.
  // A major commercial road is loud regardless of how far you walk on it.
  const areaSignal = totalWeightedSignal / totalLength;

  // Distance bonus: moving further adds a smaller additional signal boost.
  // Uses sqrt curve so short moves still register most of the distance bonus.
  const distanceRatio = Math.min(1, totalLength / tuning.distanceNormalization);
  const distanceBonus = Math.sqrt(distanceRatio);

  // Blend: area provides the base (70%), distance adds up to 30% more.
  // This is the pre-modifier "terrain signal" — what the location itself
  // would emit with no buffs applied.
  const areaWeight = tuning.signalAreaWeight;    // default 0.7
  const distWeight = tuning.signalDistanceWeight; // default 0.3
  const rawTerrain = areaSignal * areaWeight + areaSignal * distWeight * distanceBonus;

  // Apply the signal floor to the *terrain* signal, BEFORE buffs.
  // This means the floor only guarantees that non-silent terrain registers at
  // least some baseline reading — it does NOT prevent reduction buffs like
  // Quick Step / Fade / Disguise from cutting that reading further. Buffs
  // now get their full advertised effect on weak terrain instead of being
  // throttled by the floor. Park zones (rawTerrain === 0) skip the floor
  // entirely and stay fully silent.
  const flooredTerrain = rawTerrain > 0 ? Math.max(rawTerrain, tuning.baseSignalFloor) : 0;

  // Now apply the full combined modifier (card base × buffs × vehicle ×
  // disguise × passives). Hard-zero cases (Ghost, Cold Exit, Jammer, Shortcut,
  // Fade-in-residential) are already handled by the early-return above.
  const modulated = flooredTerrain * cardSignalModifier;

  // Clamp to 0-1
  return Math.min(1, Math.max(0, modulated));
}

/**
 * Calculate idle signal — generated just from standing in a location.
 * Based on the nearest edge's road classification and zone.
 * Idle signal is weaker than movement signal.
 */
function calculateIdleSignalStrength(
  nearestEdge: NavEdge | null,
): number {
  if (!nearestEdge) return tuning.minSignalThreshold; // bare minimum

  const zoneMultiplier = tuning.zoneMultipliers[nearestEdge.zone] ?? 0.6;
  // Idle signal is based on how exposed the location is
  // Standing on a major commercial street = moderate signal
  // Standing on a quiet residential footpath = near zero
  const rawStrength = nearestEdge.signalWeight * zoneMultiplier * tuning.idleDampening;
  return Math.min(1, Math.max(0, rawStrength));
}

/**
 * Convert a signal strength (0-1) to a radius in meters.
 * Higher strength = smaller (more precise) radius.
 *
 * Uses a smooth power curve:
 *   radius = minR + (maxR - minR) * (1 - strength)^curve
 *
 * With curve > 1, weak signals are pulled inward (tighter than linear).
 * With curve < 1, weak signals spread out more.
 */
function strengthToRadius(strength: number): number {
  const minR = tuning.minSearchRadius;
  const maxR = tuning.maxSearchRadius;
  const curve = tuning.searchRadiusCurve;

  // Clamp strength to 0-1
  const s = Math.max(0, Math.min(1, strength));

  // (1 - strength)^curve: at strength=1 → 0, at strength=0 → 1
  const t = Math.pow(1 - s, curve);

  return minR + (maxR - minR) * t;
}

/**
 * Offset the signal center from the actual position so the actual position
 * is somewhere within the radius but not at the center.
 * Biased toward the inner 70% so the position isn't always at the edge.
 */
function offsetSignalCenter(
  actualPosition: LatLng,
  radius: number,
): LatLng {
  // Random distance from center: biased toward 20-70% of radius
  const distFraction = 0.2 + Math.random() * 0.5; // 20-70% of radius
  const offsetDist = radius * distFraction;

  // Random angle
  const angle = Math.random() * 2 * Math.PI;

  // Convert offset distance (meters) to lat/lng delta
  const dLat = (offsetDist * Math.cos(angle)) / 111000;
  const dLng = (offsetDist * Math.sin(angle)) / (111000 * Math.cos((actualPosition.lat * Math.PI) / 180));

  return {
    lat: actualPosition.lat + dLat,
    lng: actualPosition.lng + dLng,
  };
}

/**
 * Find the nearest edge to a position (for idle signal calculation).
 */
export function findNearestEdgeToPosition(
  lat: number,
  lng: number,
  edges: NavEdge[],
): NavEdge | null {
  let bestDist = Infinity;
  let bestEdge: NavEdge | null = null;

  for (const edge of edges) {
    const geom = edge.geometry;
    for (let i = 0; i < geom.length - 1; i++) {
      const [ax, ay] = geom[i];     // lng, lat
      const [bx, by] = geom[i + 1];
      const dx = bx - ax;
      const dy = by - ay;
      const lenSq = dx * dx + dy * dy;
      if (lenSq === 0) continue;

      let t = ((lng - ax) * dx + (lat - ay) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));

      const px = ax + t * dx;
      const py = ay + t * dy;
      const d = (lng - px) ** 2 + (lat - py) ** 2;

      if (d < bestDist) {
        bestDist = d;
        bestEdge = edge;
      }
    }
  }

  return bestEdge;
}

/**
 * Compute the final post-everything signal strength for a movement along a
 * path. This is the single source of truth used by BOTH the movement preview
 * in the map hover banner AND the real signal generation in
 * handleMovementComplete — so the number the player sees while placing a move
 * matches the number they actually generate when they commit.
 *
 * The pipeline:
 *   1. Start with the card's own signal modifier (Shortcut=0, etc.)
 *   2. Multiply by active fieldcraft buffs (Quick Step, Fade, Sprint ...)
 *      — Fade is conditional on ending in a residential zone
 *   3. 2× on major-road length if in a hotwired vehicle
 *   4. 0 if ghostActive, coldExitTurnsLeft, inside jammer zone
 *   5. Proportional reduction for Disguise on commercial/major segments
 *   6. Multiply by background-bucket passives (caller-computed so signal.ts
 *      doesn't have to know about passives.ts)
 *   7. Feed into calculatePathSignalStrength + exposureToMultiplier to get
 *      the final 0..1 strength
 *
 * Returns the effective modifier, raw pre-exposure strength, and the final
 * clamped strength. Callers can use `finalStrength` for UI / actual signal;
 * `effectiveMod` is useful for debugging tooltips.
 */
export function computeMovementSignalStrength(params: {
  edges: NavEdge[];
  cardSignalModifier: number;
  activeBuffs: MovementBuff[];
  isInVehicle: boolean;
  ghostActive: boolean;
  coldExitTurnsLeft: number;
  jammerTurnsLeft: number;
  jammerPosition: LatLng | null;
  jammerRadius: number;
  disguiseTurnsLeft: number;
  endPos: LatLng;
  endZone: ZoneType | null;
  exposure: number;
  /** Background-bucket passive multiplier, pre-computed by the caller. */
  passiveMultiplier: number;
}): { effectiveMod: number; baseStrength: number; finalStrength: number } {
  let mod = params.cardSignalModifier;

  // Active fieldcraft buffs
  for (const buff of params.activeBuffs) {
    if (buff.signalCondition === 'residential-zone') {
      // Fade: only zeroes signal if ending in a residential zone
      if (params.endZone === 'residential') {
        mod = buff.signalModifier ?? 0;
      }
    } else if (buff.signalModifier !== undefined) {
      mod *= buff.signalModifier;
    }
  }

  // Vehicle on major roads: double signal (proportional-ish — any major road
  // length in the path doubles the whole mod; matches handleMovementComplete).
  if (params.isInVehicle) {
    const majorLength = params.edges
      .filter(e => e.classification === 'major')
      .reduce((sum, e) => sum + e.length, 0);
    if (majorLength > 0) mod *= 2;
  }

  // Ghost / Cold Exit / Jammer silence the move entirely
  if (params.ghostActive) mod = 0;
  if (params.coldExitTurnsLeft > 0) mod = 0;
  if (params.jammerTurnsLeft > 0 && params.jammerPosition) {
    const jdLat = (params.endPos.lat - params.jammerPosition.lat) * 111000;
    const jdLng = (params.endPos.lng - params.jammerPosition.lng) * 111000 * Math.cos((params.endPos.lat * Math.PI) / 180);
    if (Math.sqrt(jdLat * jdLat + jdLng * jdLng) <= params.jammerRadius) mod = 0;
  }

  // Disguise: proportional 75% reduction on commercial/major segments
  if (params.disguiseTurnsLeft > 0 && params.edges.length > 0) {
    const totalLen = params.edges.reduce((s, e) => s + e.length, 0);
    if (totalLen > 0) {
      const maskedLen = params.edges
        .filter(e => e.zone === 'commercial' || e.classification === 'major')
        .reduce((s, e) => s + e.length, 0);
      const fraction = maskedLen / totalLen;
      const reduction = fraction * (1 - tuning.disguiseSignalMultiplier);
      mod *= 1 - reduction;
    }
  }

  // Background-bucket passives (pre-computed by the caller so signal.ts
  // doesn't have to import passives.ts)
  mod *= params.passiveMultiplier;

  // Pipe through the base strength + exposure multiplier
  const baseStrength = calculatePathSignalStrength(params.edges, mod);
  const expMult = exposureToMultiplier(params.exposure);
  const finalStrength = Math.min(1, Math.max(0, baseStrength * expMult));

  return { effectiveMod: mod, baseStrength, finalStrength };
}

// --- Public API ---

/**
 * Generate a signal from movement along a path.
 * Returns null if the signal is too weak to register.
 */
export function generateMovementSignal(
  actualPosition: LatLng,
  pathEdges: NavEdge[],
  source: SignalSource,
  turn: number,
  cardSignalModifier: number = 1,
  exposure: number = 0,
): Signal | null {
  const baseStrength = calculatePathSignalStrength(pathEdges, cardSignalModifier);
  const pubMult = exposureToMultiplier(exposure);
  const strength = Math.min(1, baseStrength * pubMult);

  if (strength < tuning.minSignalThreshold) return null;

  const radius = strengthToRadius(strength);
  const position = offsetSignalCenter(actualPosition, radius);

  return {
    id: `signal-${signalCounter++}`,
    position,
    actualPosition,
    radius,
    strength,
    age: 0,
    turnGenerated: turn,
    source,
    type: 'passive',
    revealed: false, // not visible to opponent until next turn
  };
}

/**
 * Generate an idle signal from standing still at a position.
 * Even standing still generates some signal unless suppressed.
 * Returns null only if signal is below threshold.
 */
export function generateIdleSignal(
  actualPosition: LatLng,
  edges: NavEdge[],
  source: SignalSource,
  turn: number,
  signalModifier: number = 1,
  exposure: number = 0,
): Signal | null {
  if (signalModifier === 0) return null;

  const nearestEdge = findNearestEdgeToPosition(actualPosition.lat, actualPosition.lng, edges);
  const rawStrength = calculateIdleSignalStrength(nearestEdge);
  const pubMult = exposureToMultiplier(exposure);
  const strength = Math.min(1, rawStrength * signalModifier * pubMult);

  if (strength < tuning.minSignalThreshold) return null;

  const radius = strengthToRadius(strength);
  const position = offsetSignalCenter(actualPosition, radius);

  return {
    id: `signal-${signalCounter++}`,
    position,
    actualPosition,
    radius,
    strength,
    age: 0,
    turnGenerated: turn,
    source,
    type: 'passive',
    revealed: false,
  };
}

/**
 * Reveal pending signals from the opponent and age all existing signals.
 * Called at the END of the player's turn, after the agent worker has
 * generated its response-turn signals. At that point the turn counter has
 * already advanced to `currentTurn` AND the freshly-generated agent signals
 * are tagged with `currentTurn` too — so we reveal everything up to and
 * including `currentTurn`, not strictly before.
 *
 * - Signals from the opponent's last turn become visible (revealed = true)
 * - Already-visible signals age by 1
 * - Signals past SIGNAL_MAX_AGE are removed
 *
 * @param currentTurn - The turn that is just starting
 * @param viewerSource - Who is viewing: 'player' sees agent signals, 'agent' sees player signals
 */
export function revealAndAgeSignals(
  signals: Signal[],
  currentTurn: number,
  viewerSource: 'player' | 'agent',
): Signal[] {
  const result: Signal[] = [];

  for (const signal of signals) {
    // Determine if this signal should be visible to the viewer
    // Player sees agent signals, agent sees player signals
    // Each side's own signals are always visible to themselves immediately
    const isOwnSignal = signal.source === viewerSource;
    const isOpponentSignal = !isOwnSignal && signal.source !== 'decoy';

    if (isOpponentSignal && !signal.revealed) {
      // Opponent signal from this turn or earlier — reveal it now.
      // The old check was `< currentTurn` which missed agent signals tagged
      // with the new turn value (the turn was advanced before the worker
      // ran), causing Blown Entrance / Radio Scanner / normal observation
      // to show zero signals on turns 1–3 where "turn 1 agent activity"
      // should have been visible.
      if (signal.turnGenerated <= currentTurn) {
        result.push({
          ...signal,
          revealed: true,
          age: 0, // freshly revealed
        });
      } else {
        // Generated for a future turn (shouldn't happen) — keep hidden
        result.push(signal);
      }
    } else if (signal.revealed) {
      // Already revealed — age it
      const newAge = signal.age + 1;
      const maxAge = signal.maxAgeOverride ?? SIGNAL_MAX_AGE;
      if (newAge <= maxAge) {
        result.push({ ...signal, age: newAge });
      }
      // else: too old, remove
    } else {
      // Own signal or decoy — keep as-is
      result.push(signal);
    }
  }

  return result;
}

/**
 * Get only the signals that should be rendered on the map for a given viewer.
 * The viewer sees:
 * - Their own signals (always, for feedback)
 * - Opponent signals that have been revealed
 * Does NOT show unrevealed opponent signals.
 */
export function getVisibleSignals(
  signals: Signal[],
  viewer: 'player' | 'agent',
): Signal[] {
  return signals.filter(s => {
    if (s.source === viewer) return true;  // own signals
    if (s.source === 'decoy') return true; // decoys visible to everyone
    return s.revealed; // opponent signals only if revealed
  });
}

/**
 * Determine the player's awareness tier based on their current exposure.
 * Higher exposure = more awareness of agent signals (but also more visible to agent).
 *
 * Returns: 'blind' | 'partial' | 'full'
 * - blind: can't see any passive agent signals
 * - partial: only see weak agent signals (strength < 0.6)
 * - full: see all agent signals
 *
 * Intel cards (Sweep, Counter-Surveillance, Informant, Spotter) always bypass this.
 */
/**
 * Awareness scales WITH exposure.
 * Low exposure = weak awareness → agent signals appear heavily dampened (large radius).
 * High exposure = strong awareness → agent signals appear closer to true strength.
 *
 * Continuous scale — no hard gates. Even at 0 exposure you see agent signals,
 * they're just very imprecise. Dampening reduces smoothly as exposure climbs.
 *
 * Tiers are cosmetic labels for UI display only.
 */
export function getAwarenessTier(exposure: number): 'blind' | 'partial' | 'full' {
  const thresholds = tuning.awarenessThresholds;
  if (exposure >= thresholds.full) return 'full';
  if (exposure >= thresholds.partial) return 'partial';
  return 'blind';
}

/**
 * Get the awareness-based signal strength multiplier.
 * Below the blind threshold, returns 0 (player has no awareness — agent signals hidden).
 * Between blind and full thresholds, scales from awarenessMinMultiplier up to 1.0.
 * At or above full threshold, returns 1 (signals shown at true strength).
 */
export function getAwarenessMultiplier(exposure: number): number {
  const { blind, full } = tuning.awarenessThresholds;
  // Below blind threshold: no awareness of agent signals at all.
  if (exposure < blind) return 0;
  if (exposure >= full) return 1;
  // Between blind and full: scale from awarenessMinMultiplier up to 1.0.
  const range = full - blind;
  if (range <= 0) return 1;
  const t = Math.min(1, Math.max(0, (exposure - blind) / range));
  return tuning.awarenessMinMultiplier + (1 - tuning.awarenessMinMultiplier) * t;
}
